import React, { ReactNode } from 'react';
import { Box } from '@material-ui/core';
import { GridCellCtx, GridColumnConfig } from '../alternative-calculation/config/columns';
import { ColumnKey } from '../alternative-calculation/types/alternative-calculation.types';
import { CaseCalculationPart } from '../../interfaces/Case';

// Cell renderers supplied by OrderDetailsNew (close over its handlers/state).
export interface OrderGridColumnsCtx {
  t: (key: string, def: string) => string;
  showInvoiceColumns: boolean;
  showActionsColumn: boolean;
  renderId: (part: CaseCalculationPart) => ReactNode;
  renderCodeOe: (part: CaseCalculationPart) => ReactNode;
  renderCodeAm: (part: CaseCalculationPart) => ReactNode;
  renderName: (part: CaseCalculationPart) => ReactNode;
  renderQuality: (part: CaseCalculationPart) => ReactNode;
  renderUnitPrice: (part: CaseCalculationPart) => ReactNode;
  renderPieceNr: (
    part: CaseCalculationPart,
    onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) => ReactNode;
  renderDiscount: (part: CaseCalculationPart) => ReactNode;
  renderTotalPrice: (part: CaseCalculationPart) => ReactNode;
  renderProducer: (part: CaseCalculationPart) => ReactNode;
  renderDelivery: (part: CaseCalculationPart) => ReactNode;
  renderExpectedDelivery: (part: CaseCalculationPart) => ReactNode;
  renderDeliveryNotice: (part: CaseCalculationPart) => ReactNode;
  renderInvoiceNumber: (part: CaseCalculationPart) => ReactNode;
  renderStatus: (part: CaseCalculationPart) => ReactNode;
  renderRowActions: (part: CaseCalculationPart) => ReactNode;
  renderActions: (part: CaseCalculationPart) => ReactNode;
}

// Cell wrapper; no stopPropagation so clicks bubble to the row toggle (guard lives in the header).
const cell = (node: ReactNode): ReactNode => {
  const isText = typeof node === 'string' || typeof node === 'number';
  return (
    <Box style={{ minWidth: 0, ...(isText ? { paddingRight: 5, wordBreak: 'break-word' } : {}) }}>
      {node}
    </Box>
  );
};

// Column config for the order-details grid (order part row); expanded alternatives keep the alt-calc set.
export const buildOrderGridColumns = (ctx: OrderGridColumnsCtx): GridColumnConfig[] => [
  {
    key: 'id',
    width: '48px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_ID_LABEL', 'id'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderId(part))
  },
  {
    key: 'guideNumber',
    width: '150px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_OE_CODE_LABEL', 'Cod OE'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderCodeOe(part))
  },
  {
    key: 'alternativeGuideNumber',
    width: '150px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_SUPPLIER_CODE_LABEL', 'Cod Furnizor'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderCodeAm(part))
  },
  {
    key: 'name',
    width: 'minmax(140px,1fr)',
    sortKey: ColumnKey.Name,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_NAME_LABEL', 'Denumire'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderName(part))
  },
  {
    key: 'quality',
    width: '92px',
    sortKey: ColumnKey.Quality,
    filterable: true,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_QUALITY_LABEL', 'Calitate'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderQuality(part))
  },
  {
    key: 'originalPrice',
    width: '104px',
    sortKey: ColumnKey.Price,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_ORIGINAL_PRICE_LABEL', 'Pret unitar'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderUnitPrice(part))
  },
  {
    key: 'pieceNr',
    width: '80px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_PIECE_NR_LABEL', 'Unitati'),
    renderBodyCell: ({ part, onQuantityChange }: GridCellCtx) =>
      cell(ctx.renderPieceNr(part, onQuantityChange))
  },
  {
    key: 'discount',
    width: '90px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_DISCOUNT_LABEL', 'Discount (%)'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderDiscount(part))
  },
  {
    key: 'totalPrice',
    width: '110px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_TOTAL_PRICE_LABEL', 'Pret total'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderTotalPrice(part))
  },
  {
    key: 'producer',
    width: '120px',
    sortKey: ColumnKey.Producer,
    filterable: true,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_PRODUCER_LABEL', 'Producator'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderProducer(part))
  },
  {
    key: 'remarks',
    width: '120px',
    sortKey: ColumnKey.Delivery,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_DELIVERY_TIME_LABEL', 'Livrare'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderDelivery(part))
  },
  {
    key: 'expectedDeliveryDate',
    width: '120px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_EXPECTED_DELIVERY_LABEL', 'Data livrare'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderExpectedDelivery(part))
  },
  ...(ctx.showInvoiceColumns
    ? [
        {
          key: 'deliveryNotice',
          width: '104px',
          sortKey: null,
          filterable: false,
          numeric: false,
          renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_DELIVERY_NOTICE_LABEL', 'Nr. aviz'),
          renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderDeliveryNotice(part))
        },
        {
          key: 'invoiceNumber',
          width: '104px',
          sortKey: null,
          filterable: false,
          numeric: false,
          renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_INVOICE_NUMBER_LABEL', 'Factura AI'),
          renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderInvoiceNumber(part))
        }
      ]
    : []),
  {
    key: 'status',
    width: '110px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => ctx.t('ORDER_PART_DETAILS_STATUS_LABEL', 'Status'),
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderStatus(part))
  },
  {
    key: 'rowActions',
    width: '96px',
    sortKey: null,
    filterable: false,
    numeric: false,
    renderHeaderCell: () => '',
    renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderRowActions(part))
  },
  ...(ctx.showActionsColumn
    ? [
        {
          key: 'actions',
          width: '132px',
          sortKey: null,
          filterable: false,
          numeric: false,
          renderHeaderCell: () => ctx.t('ORDER_PART_ACTIONS_LABEL', 'Actiuni'),
          renderBodyCell: ({ part }: GridCellCtx) => cell(ctx.renderActions(part))
        }
      ]
    : [])
];
